import { apiClient } from './api'

// Inventory interfaces
export type ResourceComposition = {
  cpu?: number
  memory?: number
  storage?: number
}

export type ClusterResource = {
  total_capacity: number
  allocated_capacity: number // gerenciado (admissão: modal de reserva)
  reserved_capacity: number
  available_capacity: number // gerenciado (admissão)
  utilization_percentage: number
  unit: string
  // display (uso observado no Proxmox) — os cards leem estes; opcionais p/ rollout
  observed_allocated_capacity?: number
  used_display?: number
  available_display?: number
  utilization_display?: number
}

export type InventorySummary = {
  cluster_id: string
  cluster_name: string
  cluster_status: string
  resources: {
    cpu: ClusterResource
    memory: ClusterResource
    storage: ClusterResource
  }
  last_updated: string
}

export type AllocationRequest = {
  cluster_id: string
  resource_composition: ResourceComposition
  product_id?: string
  contract_id?: string
  description?: string
}

export type TransactionResponse = {
  id: string
  transaction_type: 'allocation' | 'deallocation' | 'reservation'
  resource_type: 'cpu' | 'memory' | 'storage'
  amount: number
  description: string
  product_id?: string
  contract_id?: string
  user_id: string
  created_at: string
  transaction_metadata?: Record<string, any>
}

export type AllocationResponse = {
  success: boolean
  message: string
  transactions: TransactionResponse[]
  updated_inventory: InventorySummary
}

export type DeallocationRequest = {
  cluster_id: string
  resource_composition: ResourceComposition
  product_id?: string
  contract_id?: string
  description?: string
}

export type ReservationRequest = {
  cluster_id: string
  resource_composition: ResourceComposition
  description?: string
}

export type HistoryResponse = {
  id: string
  resource_type: 'cpu' | 'memory' | 'storage'
  sync_date: string
  old_total_capacity: number
  new_total_capacity: number
  capacity_change: number
  change_reason: string
  description?: string
  synced_by?: string
}

export type SyncRequest = {
  force?: boolean
}

export type SyncResponse = {
  success: boolean
  message: string
  synced_at?: string
  nodes_synced?: number
  resources_updated?: number
  inventory_updates?: number
  cluster_info?: Record<string, any>
  inventory_summary?: InventorySummary
  error?: string
}

export type TrendsResponse = {
  cluster_id: string
  trends: Record<string, any>
  period_days: number
}

export type InventoryResourceType = 'cpu' | 'memory' | 'storage'
export type TransactionType = 'allocation' | 'deallocation' | 'reservation'

export const inventoryApi = {
  // Get organization inventory summary (all clusters)
  getOrganizationInventory: (): Promise<InventorySummary[]> => {
    return apiClient.get('/api/v1/inventory/summary')
  },

  // Get inventory summary for a cluster
  getInventorySummary: (clusterId: string): Promise<InventorySummary> => {
    return apiClient.get(`/api/v1/inventory/clusters/${clusterId}/summary`)
  },

  // Allocate resources
  allocateResources: (request: AllocationRequest): Promise<AllocationResponse> => {
    return apiClient.post('/api/v1/inventory/allocate', request)
  },

  // Deallocate resources
  deallocateResources: (request: DeallocationRequest): Promise<AllocationResponse> => {
    return apiClient.post('/api/v1/inventory/deallocate', request)
  },

  // Reserve resources temporarily
  reserveResources: (request: ReservationRequest): Promise<AllocationResponse> => {
    return apiClient.post('/api/v1/inventory/reserve', request)
  },

  // Get inventory change history
  getInventoryHistory: (
    clusterId: string,
    resourceType?: InventoryResourceType,
    limit: number = 50,
  ): Promise<HistoryResponse[]> => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (resourceType) {
      params.append('resource_type', resourceType)
    }
    return apiClient.get(`/api/v1/inventory/clusters/${clusterId}/history?${params}`)
  },

  // Get resource transactions
  getResourceTransactions: (
    clusterId: string,
    transactionType?: TransactionType,
    productId?: string,
    limit: number = 100,
  ): Promise<TransactionResponse[]> => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (transactionType) {
      params.append('transaction_type', transactionType)
    }
    if (productId) {
      params.append('product_id', productId)
    }
    return apiClient.get(`/api/v1/inventory/clusters/${clusterId}/transactions?${params}`)
  },

  // Sync cluster inventory with Proxmox (long operation)
  syncClusterInventory: (clusterId: string, request: SyncRequest = {}): Promise<SyncResponse> => {
    return apiClient.postLong(`/api/v1/inventory/clusters/${clusterId}/sync`, request)
  },

  // Get utilization trends
  getUtilizationTrends: (clusterId: string, days: number = 30): Promise<TrendsResponse> => {
    return apiClient.get(`/api/v1/inventory/clusters/${clusterId}/trends?days=${days}`)
  },
}

export default inventoryApi
